import React, { useEffect, useState } from "react";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { Flame, Sparkles, Trophy, Crown, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Leaderboard() {
  const [users, setUsers] = useState([]);
  const [range, setRange] = useState("all");
  const [classFilter, setClassFilter] = useState("all"); // admin-only
  const [classes, setClasses] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/classes").then((r) => setClasses(r.data.classes || [])).catch(() => {});
  }, [user?.is_admin]);

  useEffect(() => {
    const params = new URLSearchParams({ range });
    if (user?.is_admin && classFilter !== "all") params.set("class_year", classFilter);
    api.get(`/leaderboard?${params.toString()}`).then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  }, [range, classFilter, user?.is_admin]);

  // Non-admin students: label reflects their assigned class (backend enforces scope).
  const studentClassLabel =
    !user?.is_admin && user?.class_year != null ? `Class ${user.class_year}` : null;

  return (
    <div className="min-h-screen grain-bg" data-testid="leaderboard-page">
      <Nav />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-8 text-center">
          <div className="inline-flex brutal-badge bg-brand-yellow mb-3">
            <Trophy className="w-3.5 h-3.5" strokeWidth={3} />
            {studentClassLabel ? `${studentClassLabel} ranks` : "Global ranks"}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold text-brand-navy">Leaderboard</h1>
          <p className="text-brand-navy/70 mt-2 text-lg">
            {studentClassLabel ? "Your class only." : "Where do you stand?"}
          </p>

          <div className="inline-flex mt-6 border-2 border-brand-ink rounded-full bg-white shadow-brutal p-1">
            <button
              onClick={() => setRange("week")}
              data-testid="lb-toggle-week"
              className={`px-5 py-2 rounded-full font-bold text-sm ${range === "week" ? "bg-brand-yellow" : ""}`}
            >
              This week
            </button>
            <button
              onClick={() => setRange("all")}
              data-testid="lb-toggle-all"
              className={`px-5 py-2 rounded-full font-bold text-sm ${range === "all" ? "bg-brand-yellow" : ""}`}
            >
              All-time
            </button>
          </div>

          {user?.is_admin && (
            <div className="mt-4 inline-flex items-center gap-2 flex-wrap justify-center">
              <span className="brutal-badge bg-brand-pink text-white text-xs">
                <ShieldCheck className="w-3 h-3" strokeWidth={3} /> Admin view
              </span>
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                data-testid="lb-class-filter"
                className="brutal-input py-2 text-sm font-bold"
              >
                <option value="all">All classes</option>
                {classes.map((c) => (
                  <option key={c.class_year} value={String(c.class_year)}>
                    Class {c.class_year}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {users.length >= 3 && (
          <div className="grid grid-cols-3 gap-4 md:gap-6 mb-10">
            {[users[1], users[0], users[2]].map((u, i) => {
              const positions = [2, 1, 3];
              const bgs = ["bg-brand-mint", "bg-brand-yellow", "bg-brand-pink"];
              const heights = ["h-40", "h-52", "h-36"];
              const pos = positions[i];
              return (
                <div key={u.id} className="flex flex-col items-center">
                  <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-brand-ink shadow-brutal flex items-center justify-center font-display font-bold text-2xl mb-2 ${bgs[i]} ${pos === 3 ? "text-white" : "text-brand-navy"}`}>
                    {pos === 1 && <Crown className="w-8 h-8" strokeWidth={2.5} />}
                    {pos !== 1 && u.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="font-display font-bold text-brand-navy truncate max-w-full">{u.name}</div>
                  <div className="text-sm font-bold text-brand-navy/70 mb-2">{u.xp} XP</div>
                  <div className={`w-full ${heights[i]} ${bgs[i]} border-2 border-brand-ink shadow-brutal rounded-t-2xl flex items-start justify-center pt-3 font-display text-3xl font-bold`}>
                    #{pos}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="brutal-card overflow-hidden bg-white">
          <div className="divide-y-2 divide-brand-ink/20">
            {users.length === 0 && (
              <div className="p-10 text-center text-brand-navy/70">
                No learners on the {range === "week" ? "weekly" : "all-time"} board yet — be the first!
              </div>
            )}
            {users.map((u, i) => {
              const isMe = user && u.id === user.id;
              return (
                <div key={u.id} data-testid={`lb-row-${i}`} className={`flex items-center gap-4 p-4 md:p-5 ${isMe ? "bg-brand-yellow" : ""}`}>
                  <div className="w-10 h-10 rounded-full border-2 border-brand-ink flex items-center justify-center font-bold text-sm bg-brand-cream">
                    {i + 1}
                  </div>
                  <div className="w-10 h-10 rounded-full border-2 border-brand-ink bg-brand-blue text-white flex items-center justify-center font-display font-bold">
                    {u.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-brand-navy truncate">{u.name} {isMe && <span className="text-xs text-brand-pink">(you)</span>}</div>
                  </div>
                  {user?.is_admin && u.class_year != null && (
                    <div className="brutal-badge bg-brand-blue text-white text-xs">Class {u.class_year}</div>
                  )}
                  <div className="brutal-badge bg-brand-pink text-white text-xs"><Flame className="w-3 h-3" strokeWidth={3} /> {u.streak}</div>
                  <div className="brutal-badge bg-brand-mint text-xs"><Sparkles className="w-3 h-3" strokeWidth={3} /> {u.xp} XP</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
